import React, { useState, useEffect, useMemo, forwardRef, useImperativeHandle, useRef } from 'react';
import { MessageList } from './streaming/MessageList';
import { ChatHeader } from './streaming/ChatHeader';
import { InputArea } from './streaming/InputArea';
import { TypingIndicator } from './TypingIndicator';
import { ErrorBoundary } from '../ui/ErrorBoundary';
import { ChatErrorBoundary } from './ChatErrorBoundary';
import { useUnifiedChat } from '@/hooks/useUnifiedChat';
import { Send, Maximize2, Minimize2, X, ChevronDown } from "lucide-react";
import { v4 as uuidv4 } from 'uuid';
import { ChatTheme, Question, TriggerRule } from "@/types/theme";
import { createNewSessionId } from '@/utils/chatLocalStorage';
import { Button } from "@/components/ui/button";
import { getPiiData, processPiiVariables } from '@/utils/piiUtils';
import { chatLogger } from "@/utils/logger";
import { usePerformanceMonitor } from "@/utils/performance";

// Import chat widget styles
import "./UnifiedChatWidget.css";

export interface UnifiedChatWidgetProps {
  chatbotId?: string;
  theme?: ChatTheme;
  questions?: Question[];
  triggerRules?: TriggerRule[];
  welcomeMessage?: string;
  completionMessage?: string;
  operatorName?: string;
  avatarUrl?: string | null; // Operator avatar - shown in chat message bubbles (NOT the bot avatar from builder)
  companyName?: string | null;
  companyLogo?: string | null;
  title?: string;
  initialOpen?: boolean;
  messageDelay?: number;
  buttonOptionsDelay?: number;
  noResponseDelay?: number;
  optionsHandlerDelay?: number;
  validationConfig?: {
    enabled?: boolean;
    maxAttempts?: number;
    strictValidation?: boolean;
  };
  onWebhookSent?: (data: any) => void;
  onClose?: () => void;
  isFullscreen?: boolean;
  className?: string;
  piiToken?: string; // Token for accessing PII data
}

export interface UnifiedChatWidgetHandle {
  resetChat: () => void;
  openChat: () => void;
  closeChat: () => void;
  toggleChat: () => void;
}

/**
 * Unified Chat Widget that combines traditional and enhanced chat experiences
 * Supports both structured flows and AI-powered conversations
 */
// PII processing function is now imported from utils

// Process questions to replace PII variables
const processPiiInQuestions = (questions: Question[], piiData: Record<string, any> | null): Question[] => {
  return questions.map(question => ({
    ...question,
    text: processPiiVariables(question.text, piiData)
  }));
};

export const UnifiedChatWidget = forwardRef<UnifiedChatWidgetHandle, UnifiedChatWidgetProps>(({
  chatbotId,
  theme,
  questions = [],
  triggerRules = [],
  welcomeMessage = "Welcome! How can I help you today?",
  completionMessage = "Thank you for chatting with us!",
  operatorName = "Assistant",
  avatarUrl,
  companyName,
  companyLogo,
  title = "Chat With Us",
  initialOpen = false,
  messageDelay = 1000,
  buttonOptionsDelay = 0,
  noResponseDelay = 2000,
  optionsHandlerDelay = 10,
  validationConfig = { enabled: false, maxAttempts: 2, strictValidation: false },
  onWebhookSent,
  onClose,
  isFullscreen: initialFullscreen = false,
  className = "",
  piiToken,
}, ref) => {
  // State for the chat UI
  const [isOpen, setIsOpen] = useState(initialOpen);
  const [isFullscreen, setIsFullscreen] = useState(initialFullscreen);
  const [userMessage, setUserMessage] = useState("");
  const [isMobile, setIsMobile] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // PII data state
  const [piiData, setPiiData] = useState<Record<string, any> | null>(null);
  const [processedQuestions, setProcessedQuestions] = useState<Question[]>([]);
  const [processedWelcomeMessage, setProcessedWelcomeMessage] = useState(welcomeMessage);
  const [processedCompletionMessage, setProcessedCompletionMessage] = useState(completionMessage);
  const [isPiiProcessingComplete, setIsPiiProcessingComplete] = useState(false);
  const [isPiiDataLoaded, setIsPiiDataLoaded] = useState(false);

  // Fetch PII data if token is provided
  useEffect(() => {
    const fetchPiiData = async () => {
      try {
        // Try to get PII token from URL if not provided as prop
        const urlParams = new URLSearchParams(window.location.search);
        const urlToken = piiToken || urlParams.get('pii_token') || urlParams.get('pii');

        chatLogger.ui('PII token check:', {
          propToken: piiToken,
          urlTokenNew: urlParams.get('pii_token'),
          urlTokenOld: urlParams.get('pii'),
          finalToken: urlToken
        });

        if (!urlToken) {
          // No PII token, mark as loaded so chat can start
          setIsPiiDataLoaded(true);
          return;
        }

        // Fetch PII data using utility function
        const data = await getPiiData(urlToken);
        chatLogger.ui('PII data loaded:', Object.keys(data));
        setPiiData(data);
        setIsPiiDataLoaded(true);
      } catch (error) {
        chatLogger.error('Error fetching PII data:', error);
      }
    };

    fetchPiiData();
  }, [piiToken]);

  // Memoize questions array to prevent infinite re-renders
  const stableQuestions = useMemo(() => questions, [JSON.stringify(questions)]);

  // Process questions, welcome message, and completion message when PII data changes or is confirmed as not needed
  useEffect(() => {
    // Only process if PII data is loaded (either with data or confirmed as not needed)
    if (!isPiiDataLoaded) {
      chatLogger.ui('Waiting for PII data to load before processing messages');
      return;
    }

    chatLogger.ui('Processing PII data for messages and questions:', {
      piiDataKeys: piiData ? Object.keys(piiData) : 'No PII data',
      questionCount: stableQuestions.length,
      welcomeMessageSample: welcomeMessage.substring(0, 30) + '...',
      completionMessageSample: completionMessage.substring(0, 30) + '...'
    });

    // Always process questions with PII data (or null for default values)
    const piiProcessedQuestions = processPiiInQuestions(stableQuestions, piiData);
    setProcessedQuestions(piiProcessedQuestions);

    // Process welcome message
    const piiProcessedWelcome = processPiiVariables(welcomeMessage, piiData);
    setProcessedWelcomeMessage(piiProcessedWelcome);

    // Process completion message
    const piiProcessedCompletion = processPiiVariables(completionMessage, piiData);
    setProcessedCompletionMessage(piiProcessedCompletion);

    // Log examples of processed content
    chatLogger.ui('PII processing complete:', {
      welcomeBefore: welcomeMessage.substring(0, 30) + '...',
      welcomeAfter: piiProcessedWelcome.substring(0, 30) + '...',
      questionSample: piiProcessedQuestions.length > 0 ?
        { before: stableQuestions[0].text.substring(0, 30) + '...',
          after: piiProcessedQuestions[0].text.substring(0, 30) + '...' } :
        'No questions'
    });
    setIsPiiProcessingComplete(true);
  }, [piiData, stableQuestions, welcomeMessage, completionMessage, isPiiDataLoaded]);

  // Debug: Log questions array only once
  useEffect(() => {
    if (processedQuestions.length > 0) {
      chatLogger.ui('Questions loaded in UnifiedChatWidget:', processedQuestions.length);
    }
  }, [processedQuestions.length]);

  // Get unified chat capabilities with structured flow and AI
  const {
    messages,
    isTyping,
    flowCompleted,
    handleUserResponse,
    startConversation,
    resetConversation,
    getAIResponse,
    streamingMessageIds,
    currentQuestionIndex
  } = useUnifiedChat({
    questions: questions, // Use original questions for real-time PII processing
    welcomeMessage: processedWelcomeMessage,
    completionMessage: processedCompletionMessage,
    chatbotId,
    messageDelay,
    buttonOptionsDelay,
    noResponseDelay,
    optionsHandlerDelay,
    validationConfig,
    onWebhookSent,
    triggerRules,
    piiData, // Add piiData for real-time PII processing
    piiDataToken: piiToken // Add piiToken for real-time PII processing
  });

  // Expose methods to parent components
  useImperativeHandle(ref, () => ({
    resetChat: () => {
      if (chatbotId) {
        createNewSessionId(chatbotId);
        resetConversation();
        setUserMessage("");
      }
    },
    openChat: () => setIsOpen(true),
    closeChat: () => setIsOpen(false),
    toggleChat: () => setIsOpen(prev => !prev)
  }));

  // Detect mobile devices
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Start conversation when widget opens and PII data is loaded
  useEffect(() => {
    chatLogger.ui('Chat initialization check:', {
      isOpen,
      messagesLength: messages.length,
      isPiiProcessingComplete,
      isPiiDataLoaded,
      hasPiiData: !!piiData
    });

    if (isOpen && messages.length === 0 && isPiiProcessingComplete && isPiiDataLoaded) {
      chatLogger.ui('Starting conversation with PII data loaded');
      startConversation();
    }
  }, [isOpen, messages.length, startConversation, isPiiProcessingComplete, isPiiDataLoaded, piiData]);

  // Handle sending user messages
  const handleSendMessage = () => {
    console.log('🔥 UnifiedChatWidget.handleSendMessage called', {
      userMessage: userMessage.substring(0, 50) + '...',
      userMessageLength: userMessage.length,
      isTyping,
      flowCompleted,
      willCallHandleUserResponse: !flowCompleted,
      willCallGetAIResponse: flowCompleted
    });
  
    if (!userMessage.trim() || isTyping) {
      console.log('🔥 UnifiedChatWidget.handleSendMessage early return', {
        emptyMessage: !userMessage.trim(),
        isTyping
      });
      return;
    }

    // For question flow or free-form chat
    if (!flowCompleted) {
      console.log('🔥 UnifiedChatWidget.handleSendMessage calling handleUserResponse');
      // Submit to question flow
      handleUserResponse(userMessage);
    } else {
      console.log('🔥 UnifiedChatWidget.handleSendMessage calling getAIResponse');
      // AI chat response after flow is completed
      getAIResponse(userMessage);
    }

    setUserMessage("");
    scrollToBottom();
  };

  // Toggle fullscreen mode
  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);

    // Prevent scrolling when fullscreen on mobile
    if (isMobile) {
      if (!isFullscreen) {
        document.body.classList.add('overflow-hidden');
      } else {
        document.body.classList.remove('overflow-hidden');
      }
    }
  };

  // Handle closing the widget
  const handleClose = () => {
    setIsOpen(false);
    if (onClose) onClose();
  };

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
      setUnreadCount(0);
    }
  };

  // Performance monitoring
  const performanceMonitor = usePerformanceMonitor();

  // Cleanup effect - prevent memory leaks
  useEffect(() => {
    return () => {
      // Remove body class if component unmounts while fullscreen
      if (document.body.classList.contains('overflow-hidden')) {
        document.body.classList.remove('overflow-hidden');
      }

      // Clear any pending timeouts
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  // Add ref for scroll timeout tracking
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // If widget is not open, show the chat button
  if (!isOpen) {
    return (
      <div className="chat-button-container">
        <Button
          className="chat-button"
          onClick={() => setIsOpen(true)}
          aria-label="Open chat"
          style={{
            backgroundColor: theme?.primarycolor || '#497CFD',
            color: theme?.buttontextcolor || '#FFFFFF',
            borderRadius: theme?.borderradius ? `${theme.borderradius}px` : '0.5rem',
          }}
        >
          <span>Chat with us</span>
          {unreadCount > 0 && (
              <span
                  style={{
                    position: 'absolute',
                    top: '-5px',
                    right: '-5px',
                    backgroundColor: '#ff4757',
                    color: 'white',
                    borderRadius: '50%',
                    width: '20px',
                    height: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '12px',
                    fontWeight: 'bold'
                  }}
              >
              {unreadCount}
            </span>
          )}
        </Button>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <ChatErrorBoundary>
        <div
          className={`unified-chat-widget ${className} ${isFullscreen ? 'fullscreen' : ''}`}
          style={{
            width: isFullscreen ? '100%' : isMobile ? '100%' : '380px',
            height: isFullscreen ? '100%' : isMobile ? '100%' : '600px',
            position: isFullscreen ? 'fixed' : 'fixed',
            bottom: isFullscreen ? 0 : isMobile ? 0 : '20px',
            right: isFullscreen ? 0 : isMobile ? 0 : '20px',
            borderRadius: isFullscreen || isMobile ? 0 : theme?.borderradius ? `${theme.borderradius}px` : '0.75rem',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            // backgroundColor: theme?.backgroundcolor || '#FFFFFF',
            backgroundColor: 'transparent',
            // boxShadow: '0 4px 24px rgba(0, 0, 0, 0.12)',
            filter: 'drop-shadow(0 4px 24px rgba(0, 0, 0, 0.12))',
            zIndex: 1000,
            padding: '12px',
            transition: 'all 0.3s ease'
          }}
          role="dialog"
          aria-labelledby="chat-header-title"
        >
          {/* Chat Header */}
          <ChatHeader
            theme={theme}
            title={title}
            isFullscreen={isFullscreen}
            onClose={handleClose}
            onToggleFullscreen={toggleFullscreen}
            companyName={companyName}
            companyLogo={companyLogo}
          />

          {/* Message List */}
          {/* Debug current question before passing to MessageList */}
          {(() => {
            const currentQuestion = currentQuestionIndex < questions.length ? questions[currentQuestionIndex] : null;
            return (
              <MessageList
                messages={messages}
                streamingMessageIds={streamingMessageIds}
                isTyping={isTyping}
                theme={theme}
                operatorName={operatorName}
                avatarUrl={avatarUrl}
                typingSpeed={400}
                autoScroll={true}
                className="flex-1"
                currentQuestion={currentQuestion}
                onOptionSelect={handleUserResponse}
                showTimestamp={theme.showtimestamp || theme.showTimestamp}
                onMessageUnread={count => setUnreadCount(count)}
                onActionButtonClick={({ type, text, value }) => {
                  const label = text || (type === 'phone' ? 'Call' : 'Open link');
                  const actionValue = value || '';

                  const message =
                    type === 'phone'
                      ? `The user pressed the phone button: ${label} (${actionValue})`
                      : `The user pressed the url button: ${label} (${actionValue})`;
                  getAIResponse(message);

                  if (typeof window !== 'undefined' && actionValue) {
                    if (type === 'url') {
                      window.open(actionValue, '_blank', 'noopener,noreferrer');
                    } else if (type === 'phone') {
                      const normalized = actionValue.startsWith('tel:') ? actionValue : `tel:${actionValue}`;
                      window.open(normalized, '_self');
                    }
                  }
                }}
              />
            );
          })()}

          {/*/!* Scroll to bottom button *!/*/}
          {/*{showScrollButton && (*/}
          {/*  <button*/}
          {/*    className="scroll-bottom-button"*/}
          {/*    onClick={scrollToBottom}*/}
          {/*    aria-label="Scroll to bottom"*/}
          {/*    style={{*/}
          {/*      position: 'absolute',*/}
          {/*      bottom: '80px',*/}
          {/*      right: '20px',*/}
          {/*      backgroundColor: theme?.primarycolor || '#497CFD',*/}
          {/*      color: '#FFFFFF',*/}
          {/*      borderRadius: '50%',*/}
          {/*      width: '40px',*/}
          {/*      height: '40px',*/}
          {/*      display: 'flex',*/}
          {/*      alignItems: 'center',*/}
          {/*      justifyContent: 'center',*/}
          {/*      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',*/}
          {/*      border: 'none',*/}
          {/*      cursor: 'pointer',*/}
          {/*      zIndex: 10*/}
          {/*    }}*/}
          {/*  >*/}
          {/*    <ChevronDown size={20} />*/}
          {/*    {unreadCount > 0 && (*/}
          {/*      <span className="unread-badge">{unreadCount}</span>*/}
          {/*    )}*/}
          {/*  </button>*/}
          {/*)}*/}

          {/* Input Area */}
          <InputArea
            message={userMessage}
            setMessage={setUserMessage}
            handleSendMessage={handleSendMessage}
            isDisabled={isTyping}
            theme={theme}
            placeholder={flowCompleted ? "Type your message..." : "Type your response..."}
            autoFocus={!isMobile}
          />

          {/* Invisible div for scrolling reference */}
          <div ref={messagesEndRef} />
        </div>
      </ChatErrorBoundary>
    </ErrorBoundary>
  );
});

UnifiedChatWidget.displayName = "UnifiedChatWidget";

export default UnifiedChatWidget;
